var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BuySellAssetPageSchema } from '@shared/zod-schemas/frontend';
import { BuySellAssetControllerStages } from '@shared/types/frontend/assetPage';
import { toast } from 'sonner';
import { useSearchParams } from 'next/navigation';
import { ChainId, NATIVE_ADDRESS, NATIVE_SOL, ZERO_ADDRESS } from '@shared/utils/config/networks';
import { WrappedNativeTokens } from '@shared/types/quoting-engine/wrappedNativeTokens';
import { V2Networks } from '@shared/utils/config/networkAddresses';
import { useStableCoinsData } from '@/hooks/asset/useTokenData';
import getNetworkLogo from '@/lib/misc/getNetworkLogo';
import useParseAssetsIntoNativeAndCash from '@/hooks/asset/useParseAssetsIntoNativeAndCash';
import { useSwap } from '@/contexts/SwapProvider';
import { convertBasicTokenInfoToCovalentBalanceItem, } from '@/hooks/pricing/useBasicTokenInfo';
export var useBuySellForm = function (_a) {
    var _b, _c, _d;
    var tokenData = _a.tokenData, assetData = _a.assetData;
    var urlParams = useSearchParams();
    var _e = [
        urlParams.get('address') || ((_b = tokenData === null || tokenData === void 0 ? void 0 : tokenData.token) === null || _b === void 0 ? void 0 : _b.address),
        urlParams.get('networkId'),
        urlParams.get('tradeSide'),
    ], tokenAddress = _e[0], tokenNetwork = _e[1], tradeSideParam = _e[2];
    var _f = useSwap(), quoteData = _f.quoteData, quoteStringInfo = _f.quoteStringInfo, fromAssets = _f.fromAssets, toAssets = _f.toAssets, swapWrapper = _f.swapWrapper, swapResult = _f.swapResult, swapLoading = _f.swapLoading, triggerQuote = _f.triggerQuote, quoteRefetch = _f.quoteRefetch, resetAssets = _f.resetAssets, timeUntilRefetchQuote = _f.timeUntilRefetchQuote, handleOnRefresh = _f.handleOnRefresh, setFromAssets = _f.setFromAssets, setToAssets = _f.setToAssets;
    var _g = useState(BuySellAssetControllerStages.default), stage = _g[0], setStage = _g[1];
    var _h = useState(false), isFetching = _h[0], setIsFetching = _h[1];
    var _convertTokenData = function () {
        var _a;
        var _params = {
            token: tokenData === null || tokenData === void 0 ? void 0 : tokenData.token,
            tokenInfo: (_a = tokenData === null || tokenData === void 0 ? void 0 : tokenData.tokenInfo) === null || _a === void 0 ? void 0 : _a.results,
            prices: [tokenData === null || tokenData === void 0 ? void 0 : tokenData.prices],
        };
        var _convertedAsset = convertBasicTokenInfoToCovalentBalanceItem(_params);
        _convertedAsset.contract_address = tokenAddress !== null && tokenAddress !== void 0 ? tokenAddress : _convertedAsset.contract_address;
        var asset = assetData === null || assetData === void 0 ? void 0 : assetData.balances.find(function (token) {
            return (token.contract_address === _convertedAsset.contract_address &&
                token.network === _convertedAsset.network) ||
                (token.contract_address === tokenAddress && token.network === _convertedAsset.network);
        });
        _convertedAsset.contract_display_name = asset === null || asset === void 0 ? void 0 : asset.contract_display_name;
        _convertedAsset.contract_name = asset === null || asset === void 0 ? void 0 : asset.contract_name;
        _convertedAsset.usd_value = asset === null || asset === void 0 ? void 0 : asset.usd_value;
        _convertedAsset.balance = asset === null || asset === void 0 ? void 0 : asset.balance;
        _convertedAsset.quote_rate = asset === null || asset === void 0 ? void 0 : asset.quote_rate;
        _convertedAsset.quote = asset === null || asset === void 0 ? void 0 : asset.quote;
        _convertedAsset.formatted_balance = asset === null || asset === void 0 ? void 0 : asset.formatted_balance;
        return _convertedAsset;
    };
    var form = useForm({
        resolver: zodResolver(BuySellAssetPageSchema),
        mode: 'onChange',
        defaultValues: {
            tradeAmount: '0',
            tradeSide: tradeSideParam ? tradeSideParam : 'buy',
            fromToken: undefined,
            toToken: undefined,
        },
    });
    var _j = [
        form.watch('tradeAmount'),
        form.watch('fromToken'),
        form.watch('toToken'),
        form.watch('tradeSide'),
    ], watchTradeAmount = _j[0], watchFromToken = _j[1], watchToToken = _j[2], watchTradeSide = _j[3];
    var _assetBalance = useMemo(function () {
        if (!assetData || !tokenAddress)
            return undefined;
        return assetData.balances.find(function (token) {
            var _addressesAreEqual = token.contract_address.toLowerCase() === tokenAddress.toLowerCase();
            var _networkIsEqual = token.network === Number(tokenNetwork);
            if (_addressesAreEqual && _networkIsEqual)
                return token;
        });
    }, [assetData, tokenAddress]);
    // verify the (assets tokens & form tokens) and (assets amount & form amount) are equal
    var assetsAndFormIsEqual = useMemo(function () {
        var _a, _b, _c, _d, _e, _f, _g;
        if (fromAssets.length === 0 || toAssets.length === 0 || !watchFromToken || !watchToToken)
            return false;
        var _h = [
            (_b = (_a = fromAssets[0]) === null || _a === void 0 ? void 0 : _a.contract_address) === null || _b === void 0 ? void 0 : _b.toLowerCase(),
            (_d = (_c = toAssets[0]) === null || _c === void 0 ? void 0 : _c.contract_address) === null || _d === void 0 ? void 0 : _d.toLowerCase(),
            (_e = fromAssets[0]) === null || _e === void 0 ? void 0 : _e.amountToUseStr,
        ], _fromAssetContractAddr = _h[0], _toAssetContractAddr = _h[1], _fromAmount = _h[2];
        var _j = [
            _fromAssetContractAddr === ((_f = watchFromToken === null || watchFromToken === void 0 ? void 0 : watchFromToken.contract_address) === null || _f === void 0 ? void 0 : _f.toLowerCase()),
            _toAssetContractAddr === ((_g = watchToToken === null || watchToToken === void 0 ? void 0 : watchToToken.contract_address) === null || _g === void 0 ? void 0 : _g.toLowerCase()),
            _fromAmount === watchTradeAmount,
        ], _fromIsEqual = _j[0], _toIsEqual = _j[1], _AmountIsEqual = _j[2];
        return Boolean(_fromIsEqual && _toIsEqual && _AmountIsEqual);
    }, [fromAssets, toAssets, watchFromToken, watchToToken, watchTradeAmount]);
    var handleSelectTab = function (_) {
        triggerQuote();
        form.setValue('tradeSide', _);
        if (_ === 'buy') {
            console.log('setting to token buy', _convertTokenData());
            form.setValue('toToken', _convertTokenData());
            form.setValue('fromToken', defaultBuyToken());
        }
        if (_ === 'sell') {
            console.log('handleSelectTab', _, _assetBalance, _convertTokenData(), defaultSellToken());
            form.setValue('fromToken', _assetBalance !== null && _assetBalance !== void 0 ? _assetBalance : _convertTokenData());
            console.log('setting to token buy sell', defaultSellToken());
            form.setValue('toToken', defaultSellToken());
        }
        return;
    };
    var handleAmountChange = function (_) {
        console.log('handleAmountChange', _);
        if (_ === '') {
            form.setValue('tradeAmount', _);
            return;
        }
        if (!_)
            return;
        if (_ === null || _ === void 0 ? void 0 : _.includes('.')) {
            var parts = _.split('.');
            var integerPart = parts[0];
            var decimalPart = parts[1];
            var decimalLength = decimalPart.length;
            // Limit the decimal part to a maximum of 3 digits
            if (decimalLength > 3) {
                decimalPart = decimalPart.substring(0, 3);
            }
            // Reconstruct the value with the limited decimal part
            _ = "".concat(integerPart, ".").concat(decimalPart);
        }
        form.setValue('tradeAmount', _);
    };
    var onSubmit = function () {
        toast.dismiss();
        // setOrderSubmitted(true);
        swapWrapper();
        if (stage !== BuySellAssetControllerStages.default) {
            setStage(BuySellAssetControllerStages.default);
        }
        return;
    };
    var _k = useParseAssetsIntoNativeAndCash(assetData, (_c = tokenData === null || tokenData === void 0 ? void 0 : tokenData.token) === null || _c === void 0 ? void 0 : _c.networkId), _allCashBalances = _k._allCashBalances, _nativeNetworkBalances = _k._nativeNetworkBalances, _otherNetworkNativeBalances = _k._otherNetworkNativeBalances;
    var defaultBuyToken = function () {
        var greatestValueToken;
        // if (
        //   selectedFromToken &&
        //   allAvailableTokens.find(
        //     (token) => token.contract_address === selectedFromToken.contract_address && token.contract_address !== tokenAddress
        //   )
        // ) {
        //   return selectedFromToken;
        // }
        _nativeNetworkBalances = _nativeNetworkBalances === null || _nativeNetworkBalances === void 0 ? void 0 : _nativeNetworkBalances.filter(function (token) {
            var _a;
            if (tokenAddress == WrappedNativeTokens.get(token.network) &&
                (token.contract_address == ZERO_ADDRESS ||
                    ((_a = token.contract_address) === null || _a === void 0 ? void 0 : _a.toLowerCase()) == (NATIVE_ADDRESS === null || NATIVE_ADDRESS === void 0 ? void 0 : NATIVE_ADDRESS.toLowerCase()))) {
                return false;
            }
            if (token.contract_address !== tokenAddress && token.usd_value > 0) {
                return true;
            }
            return false;
        });
        _allCashBalances = _allCashBalances === null || _allCashBalances === void 0 ? void 0 : _allCashBalances.filter(function (token) { return token.contract_address !== tokenAddress; }).filter(function (token) { return token.network != 1; });
        _otherNetworkNativeBalances = _otherNetworkNativeBalances === null || _otherNetworkNativeBalances === void 0 ? void 0 : _otherNetworkNativeBalances.filter(function (token) { return token.contract_address !== tokenAddress; }).filter(function (token) { return token.network != 1; });
        if (_nativeNetworkBalances === null || _nativeNetworkBalances === void 0 ? void 0 : _nativeNetworkBalances.length) {
            return _nativeNetworkBalances.reduce(function (prev, current) {
                return (prev === null || prev === void 0 ? void 0 : prev.usd_value) > current.usd_value && prev.contract_address != tokenAddress
                    ? prev
                    : current;
            }, null);
        }
        if (_allCashBalances === null || _allCashBalances === void 0 ? void 0 : _allCashBalances.length) {
            {
                return _allCashBalances.reduce(function (prev, current) {
                    return (prev === null || prev === void 0 ? void 0 : prev.usd_value) > current.usd_value && prev.contract_address != tokenAddress
                        ? prev
                        : current;
                }, null);
            }
            if (_otherNetworkNativeBalances === null || _otherNetworkNativeBalances === void 0 ? void 0 : _otherNetworkNativeBalances.length) {
                return _otherNetworkNativeBalances.reduce(function (prev, current) {
                    return (prev === null || prev === void 0 ? void 0 : prev.usd_value) > current.usd_value && prev.contract_address != tokenAddress
                        ? prev
                        : current;
                }, null);
            }
            return greatestValueToken;
        }
    };
    var _tokenNetworkId = (_d = tokenData === null || tokenData === void 0 ? void 0 : tokenData.token) === null || _d === void 0 ? void 0 : _d.networkId;
    var stableCoinsData = useStableCoinsData(_tokenNetworkId).data;
    var defaultSellToken = function () {
        var filteredPrefillsForNativeTokenDefaultSells = prefillsForNativeTokenDefaultSells === null || prefillsForNativeTokenDefaultSells === void 0 ? void 0 : prefillsForNativeTokenDefaultSells.filter(function (token) { return token.network == _tokenNetworkId; }).filter(function (token) {
            var _a;
            if (tokenAddress == WrappedNativeTokens.get(token.network) &&
                (token.contract_address == ZERO_ADDRESS ||
                    ((_a = token.contract_address) === null || _a === void 0 ? void 0 : _a.toLowerCase()) == (NATIVE_ADDRESS === null || NATIVE_ADDRESS === void 0 ? void 0 : NATIVE_ADDRESS.toLowerCase()))) {
                return false;
            }
            if (token.contract_address !== tokenAddress) {
                return true;
            }
            return false;
        });
        if (filteredPrefillsForNativeTokenDefaultSells === null || filteredPrefillsForNativeTokenDefaultSells === void 0 ? void 0 : filteredPrefillsForNativeTokenDefaultSells.length) {
            return filteredPrefillsForNativeTokenDefaultSells === null || filteredPrefillsForNativeTokenDefaultSells === void 0 ? void 0 : filteredPrefillsForNativeTokenDefaultSells[0];
        }
        _allCashBalances = _allCashBalances === null || _allCashBalances === void 0 ? void 0 : _allCashBalances.filter(function (token) { return token.contract_address !== tokenAddress; }).filter(function (token) { return token.network != 1; });
        if (stableCoinsData === null || stableCoinsData === void 0 ? void 0 : stableCoinsData.length) {
            return stableCoinsData === null || stableCoinsData === void 0 ? void 0 : stableCoinsData[0];
        }
    };
    useEffect(function () {
        var _a;
        if (!((_a = assetData === null || assetData === void 0 ? void 0 : assetData.balances) === null || _a === void 0 ? void 0 : _a.length))
            return;
        var buyTokenToSet = defaultBuyToken();
        var sellTokenToSet = defaultSellToken();
        if (buyTokenToSet && watchTradeSide == 'buy' && !watchFromToken) {
            form.setValue('fromToken', buyTokenToSet);
        }
        if (sellTokenToSet && watchTradeSide == 'sell' && !watchToToken) {
            console.log('setting to token buy buy buy', _convertTokenData());
            form.setValue('toToken', sellTokenToSet);
        }
        if (watchTradeSide === 'buy' && !watchToToken) {
            console.log('setting to token buy buy', _convertTokenData());
            form.setValue('toToken', _convertTokenData());
        }
        if (watchTradeSide === 'sell' && !watchFromToken) {
            form.setValue('fromToken', _assetBalance !== null && _assetBalance !== void 0 ? _assetBalance : _convertTokenData());
        }
        return function () { };
    }, [assetData, watchTradeSide]);
    // on trade side, switch from and to
    // on confirm stage, set swap assets
    // to reach confirm stage, the form must be already valid
    useEffect(function () {
        if (watchFromToken && watchTradeAmount && watchToToken) {
            setFromAssets([
                __assign(__assign({}, watchFromToken), { amountToUseStr: watchTradeAmount, amountToUse: Number(watchTradeAmount), contract_address: (watchFromToken === null || watchFromToken === void 0 ? void 0 : watchFromToken.contract_display_name) == 'Solana'
                        ? NATIVE_SOL
                        : watchFromToken === null || watchFromToken === void 0 ? void 0 : watchFromToken.contract_address }),
            ]);
            setToAssets([watchToToken]);
        }
        return function () { };
    }, [watchFromToken, watchTradeAmount, watchToToken]);
    return {
        // form fields
        form: form,
        // url params
        tokenAddress: tokenAddress,
        tokenNetwork: tokenNetwork,
        tradeSideParam: tradeSideParam,
        // general values
        stage: stage,
        setStage: setStage,
        // quoteValidationForStages,
        assetsAndFormIsEqual: assetsAndFormIsEqual,
        timeUntilRefetchQuote: timeUntilRefetchQuote,
        isFetching: isFetching,
        setIsFetching: setIsFetching,
        // swap values
        quoteData: quoteData,
        quoteStringInfo: quoteStringInfo,
        swapResult: swapResult,
        swapLoading: swapLoading,
        quoteRefetch: quoteRefetch,
        resetAssets: resetAssets,
        // form actions
        handleSelectTab: handleSelectTab,
        handleAmountChange: handleAmountChange,
        onSubmit: onSubmit,
        handleOnRefresh: handleOnRefresh,
        defaultBuyToken: defaultBuyToken,
        defaultSellToken: defaultSellToken,
    };
};
export var prefillsForNativeTokenDefaultSells = [
    {
        network: 1399811149,
        contract_address: 'So11111111111111111111111111111111111111112',
        contract_decimals: 9,
        native_token: true,
        type: 'cryptocurrency',
        last_transferred_at: '2025-01-08T00:25:30.589Z',
        balance: '320919897',
        balance_24h: '280610711',
        formatted_balance_24: 0.280610711,
        formatted_balance: 0.320919897,
        contract_name: 'Solana',
        contract_display_name: 'Solana',
        contract_ticker_symbol: 'SOL',
        logo_url: 'https://www.tradegenius.com/static/geniusImages/network_logos/solana.svg',
        logo_urls: {
            token_logo_url: 'https://www.tradegenius.com/static/geniusImages/network_logos/solana.svg',
            protocol_logo_url: null,
            chain_logo_url: 'https://www.tradegenius.com/static/geniusImages/network_logos/solana.svg',
        },
        quote_rate_24h: 217.595111787,
        quote_24h: 61.05951902867456,
        pretty_quote_24h: '$61.06',
        quote_rate: 201.244289819,
        usd_value: 64.58329676055163,
        quote: 64.58329676055163,
        pretty_quote: '$64.58',
    },
    [
        ChainId.ETHEREUM,
        ChainId.AVALANCHE,
        ChainId.BSC,
        ChainId.POLYGON,
        ChainId.ARBITRUM,
        ChainId.OPTIMISM,
        ChainId.BASE,
    ].map(function (network) {
        return {
            network: network,
            contract_address: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
            contract_decimals: 18,
            native_token: true,
            type: 'cryptocurrency',
            last_transferred_at: '2025-01-08T01:47:41.087Z',
            balance: '41224298814845200',
            balance_24h: '44393912658599816',
            formatted_balance_24: 0.044393912658599814,
            formatted_balance: 0.0412242988148452,
            contract_name: V2Networks[network].name,
            contract_display_name: V2Networks[network].name,
            contract_ticker_symbol: V2Networks[network].nativeCurrency,
            logo_url: getNetworkLogo(network),
            logo_urls: {
                token_logo_url: getNetworkLogo(network),
                protocol_logo_url: null,
                chain_logo_url: getNetworkLogo(network),
            },
            quote_rate_24h: 3694.63885502,
            quote_24h: 164.0194746348271,
            pretty_quote_24h: '$164.02',
            quote_rate: 3394.95780161,
            usd_value: 139.95475487736059,
            quote: 139.95475487736059,
            pretty_quote: '$139.95',
        };
    }),
].flat();
